import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Table, Space, Alert, Segmented, Button, Tag,
  Tooltip as AntTooltip,
} from 'antd';
import { RightOutlined, DownOutlined, CloseOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import api from '../../libs/api';
import {
  CLASS_COLOR, INK, TOTAL_INK, NEUTRAL, FAINT, GRID, OWED, RECEIVED,
  money, moneyShort, useNarrow,
} from './pipelineShared';

/**
 * Money owed on work sold and not yet delivered, in both directions.
 *
 * The Snapshot tab is a profit view and stops at the welcome pack, because
 * that is where revenue is recognised. Everything here sits before that gate,
 * so none of it is in the P&L yet and none of it can be read off one.
 *
 * Every figure is carried two ways — what the job was designed to be worth,
 * and how much of that is still out. The pair is the point: a large total with
 * nothing outstanding is a job nearly done, the same total with all of it
 * outstanding is a job not started, and one number cannot tell them apart.
 */

/* --------------------------------------------------------------- totalling */

/**
 * Every money field on a project row, summed.
 *
 * Totalled on the page rather than asked of the server because the page
 * filters: clicking a bar or a payment method has to re-total everything above
 * and below it, and a round trip per click would make a filter feel like a
 * page load. The server sends the rows; the arithmetic is cheap.
 */
const SUMMED = [
  'customerArTotal', 'customerArOutstanding', 'customerArReceived',
  'rebateArTotal', 'rebateArOutstanding', 'rebateArReceived', 'rebateArEstimated',
  'arTotal', 'arOutstanding', 'arReceived',
  'equipmentApTotal', 'equipmentApOutstanding',
  'installApTotal', 'installApOutstanding',
  'otherApOutstanding', 'apTotal', 'apOutstanding', 'apOnBills', 'apNotYetBilled',
  'netOutstanding', 'contingency',
];

function totalise(rows) {
  const out = { count: rows.length, unbilled: { equipment: 0, installation: 0 } };
  for (const k of SUMMED) out[k] = 0;
  for (const r of rows) {
    for (const k of SUMMED) out[k] += r[k] || 0;
    for (const i of (r.unbilledItems || [])) out.unbilled[i.item] += 1;
  }
  for (const k of SUMMED) out[k] = Math.round(out[k] * 100) / 100;
  return out;
}

/* ------------------------------------------------------------------ cells */

/**
 * Outstanding is the figure being read; the total is context beside it.
 *
 * Weight, size and colour carry that difference so the two can sit adjacent
 * without a header between them saying which is which.
 */
const Money = ({ v, tone = 'outstanding', bold }) => (
  <span style={{
    fontVariantNumeric: 'tabular-nums',
    color: tone === 'total' ? TOTAL_INK : (tone === 'owed' ? OWED : INK),
    fontWeight: bold ? 600 : 400,
    fontSize: tone === 'total' ? 12 : 13,
  }}
  >
    {money(v)}
  </span>
);

/**
 * A column group header that folds its detail away.
 *
 * The summary carries thirteen money columns, which is more than anyone reads
 * at once and exactly what someone checking a figure needs. Folded it answers
 * "what is owed each way"; opened, "owed for what". The chevron sits in the
 * group header so the control is on the thing it controls.
 */
function groupTitle(label, open, toggle) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(); }}
      style={{ cursor: 'pointer', userSelect: 'none', color: INK }}
    >
      {open ? <DownOutlined style={{ fontSize: 9, marginRight: 5 }} />
        : <RightOutlined style={{ fontSize: 9, marginRight: 5 }} />}
      {label}
    </span>
  );
}

/* ------------------------------------------------------------ the summary */

function SummaryTable({ classes, totals, filter, onPickClass, narrow }) {
  const [openAr, setOpenAr] = useState(false);
  const [openAp, setOpenAp] = useState(false);

  const pair = (outKey, totKey, label) => ([
    { title: `${label} O/S`,
      dataIndex: outKey,
      key: outKey,
      align: 'right',
      render: (v) => <Money v={v} /> },
    { title: `${label} total`,
      dataIndex: totKey,
      key: totKey,
      align: 'right',
      render: (v) => <Money v={v} tone="total" /> },
  ]);

  const columns = [
    { title: '',
      dataIndex: 'cls',
      key: 'cls',
      fixed: narrow ? undefined : 'left',
      width: 128,
      render: (v, row) => (
        <Space size={6}>
          <span style={{
            width: 8, height: 8, borderRadius: 2, display: 'inline-block',
            background: CLASS_COLOR[v] || NEUTRAL,
          }}
          />
          <span style={{ color: INK, fontWeight: row.key === 'total' ? 600 : 400 }}>{v}</span>
          <span style={{ color: FAINT, fontSize: 11 }}>{row.count}</span>
        </Space>
      ) },

    { title: groupTitle('AR', openAr, () => setOpenAr(!openAr)),
      children: [
        { title: 'Total AR O/S',
          dataIndex: 'arOutstanding',
          key: 'arOutstanding',
          align: 'right',
          render: (v) => <Money v={v} bold /> },
        { title: 'Total AR',
          dataIndex: 'arTotal',
          key: 'arTotal',
          align: 'right',
          render: (v) => <Money v={v} tone="total" /> },
        ...(openAr ? [
          ...pair('customerArOutstanding', 'customerArTotal', 'Customer AR'),
          ...pair('rebateArOutstanding', 'rebateArTotal', 'Rebate AR'),
        ] : []),
      ] },

    { title: groupTitle('AP', openAp, () => setOpenAp(!openAp)),
      children: [
        { title: 'Total AP O/S',
          dataIndex: 'apOutstanding',
          key: 'apOutstanding',
          align: 'right',
          render: (v, row) => (
            <AntTooltip
              title={(
                <span>
                  {money(row.apOnBills)} on supplier bills already raised and unpaid;
                  {' '}{money(row.apNotYetBilled)} is designed cost plus GST on items
                  nothing has been invoiced for yet.
                  {row.otherApOutstanding > 0 && ` Includes ${money(row.otherApOutstanding)} of freight and other costs the quote never designed, which is why the two pairs beneath do not quite add to this.`}
                </span>
              )}
            >
              <Money v={v} tone="owed" bold />
            </AntTooltip>
          ) },
        { title: 'Total AP',
          dataIndex: 'apTotal',
          key: 'apTotal',
          align: 'right',
          render: (v) => <Money v={v} tone="total" /> },
        ...(openAp ? [
          ...pair('equipmentApOutstanding', 'equipmentApTotal', 'Equipment'),
          ...pair('installApOutstanding', 'installApTotal', 'Install'),
        ] : []),
      ] },

    { title: 'Net O/S',
      dataIndex: 'netOutstanding',
      key: 'netOutstanding',
      align: 'right',
      fixed: narrow ? undefined : 'right',
      width: 136,
      render: (v) => <Money v={v} bold tone={v < 0 ? 'owed' : 'outstanding'} /> },
  ];

  const data = [
    ...classes.map((c) => ({ key: c, cls: c, ...totals[c] })),
    { key: 'total', cls: 'All', ...totals.total },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      pagination={false}
      size="small"
      scroll={{ x: (openAr || openAp) ? 1560 : 900 }}
      onRow={(row) => ({
        onClick: () => onPickClass(row.key === 'total' ? null : row.cls),
        style: {
          cursor: 'pointer',
          background: filter.cls && filter.cls === row.cls ? '#EAF4FA' : undefined,
        },
      })}
      rowClassName={(row) => (row.key === 'total' ? 'gp-breakdown' : '')}
    />
  );
}

/* ------------------------------------------------------------- the detail */

/**
 * One project opened up: what it was designed to be worth against what has
 * actually moved, on both sides.
 *
 * The same shape as the Snapshot tab's cost breakdown and for the same reason.
 * A row of totals tells you a job is behind; only the breakdown tells you
 * which half of it is behind, and whether that half is waiting on a customer,
 * a certificate agent or an installer who has not invoiced yet.
 */
function Breakdown({ row }) {
  /**
   * Where a figure came from, and what the design said if that is not it.
   *
   * An issued invoice outranks the design, so the total shown is the invoice.
   * The designed figure still belongs beside it: the gap between the two is
   * the certificates pricing away from the quote, which is a real thing to
   * notice and invisible once only one of them is on the page.
   */
  const sourceNote = (source, designed, shown, crmNote) => {
    if (source !== 'xero') return `designed — ${crmNote}`;
    const gap = Math.round((designed || 0) - shown);
    if (!designed || Math.abs(gap) <= 1) return `from the Xero invoice; matches the designed ${money(designed)}`;
    return `from the Xero invoice; the design said ${money(designed)}, ${money(Math.abs(gap))} ${gap > 0 ? 'more' : 'less'}`;
  };


  const line = (label, designed, settled, note) => ({
    key: label,
    label,
    designed,
    settled,
    outstanding: Math.max(0, designed - settled),
    note,
  });

  const stages = row.crmStages || [];
  const arLines = [
    line(
      row.customerArSource === 'xero' ? 'Customer (PPA, via Xero)' : 'Customer',
      row.customerArTotal,
      row.customerArReceived,
      sourceNote(
        row.customerArSource,
        row.customerArDesigned,
        row.customerArTotal,
        `CRM stages: ${stages.filter((x) => x.paidAt).length} of ${stages.length} collected`,
      ),
    ),
    ...(row.rebateTotalSource === 'xero' && row.rebateArDesigned != null
      && Math.abs(row.rebateArDesigned - row.rebateArTotal) > 1
      ? [{
        key: 'rebate-designed',
        label: row.rebateBilled > 0
          ? 'Rebate total taken from the claim invoices'
          : 'Rebate total taken from the sale invoice',
        designed: row.rebateArTotal,
        settled: row.rebateArReceived,
        outstanding: row.rebateArOutstanding,
        note: row.rebateBilled > 0
          ? `the agent was claimed ${money(row.rebateBilled)}; the design expected ${money(row.rebateArDesigned)}`
          : `what the customer's invoice actually deducted; the design expected ${money(row.rebateArDesigned)}`,
        muted: true,
      }]
      : []),
    ...(row.rebateComponents || []).map((c) => ({
      key: c.label,
      label: c.label,
      // Claimed gross, received net: the gap is the agent's dollar a
      // certificate, which is a fee and not an amount still owed, so a
      // collected certificate shows nothing outstanding despite the two
      // figures differing.
      designed: c.amount,
      settled: c.received,
      outstanding: c.outstanding,
      note: c.paidAt
        ? (Math.abs(c.amount - c.received) > 1
          ? `collected ${dayjs(c.paidAt).format('D MMM YY')} — claimed ${money(c.amount)}, ${money(c.amount - c.received)} taken as the agent's fee`
          : `collected ${dayjs(c.paidAt).format('D MMM YY')}`)
        : (c.estimated
          ? 'estimate — cannot be claimed until the system is installed'
          : 'claimed, not yet settled'),
    })),
    ...(row.refund
      ? [{
        key: 'refund',
        label: 'Refund',
        designed: row.refund.total,
        settled: row.refund.received,
        outstanding: row.refund.outstanding,
        note: row.refund.processedAt
          ? `${money(row.refund.amount)} refunded ${dayjs(row.refund.processedAt).format('D MMM YY')}`
            + `${row.refund.reason ? ` — ${row.refund.reason}` : ''}`
          : `${money(row.refund.amount)} approved, not yet paid back`
            + `${row.refund.reason ? ` — ${row.refund.reason}` : ''}`,
      }]
      : []),
  ];

  /**
   * What the ledger has recognised, set against what the quote designed.
   *
   * Two amounts rather than a yes or no, because the gap between them is the
   * point: this job has two hundred dollars posted against a $2,867
   * installation, and calling that "billed" made the remaining $2,647 look
   * settled.
   */
  const billedNote = (billedInclGst, total) => {
    if (!(billedInclGst > 0)) return 'nothing billed to us yet — outstanding is the designed cost plus GST';
    if (billedInclGst >= total - 1) return 'fully billed — outstanding is what is unpaid on those bills';
    return `${money(billedInclGst)} billed so far; the rest is designed cost nobody has invoiced us for`;
  };

  const apLines = [
    line('Equipment', row.equipmentApTotal,
      row.equipmentApTotal - row.equipmentApOutstanding, billedNote(row.equipmentApBilled, row.equipmentApTotal)),
    line('Installation', row.installApTotal,
      row.installApTotal - row.installApOutstanding, billedNote(row.installApBilled, row.installApTotal)),
    ...(row.otherApOutstanding > 0
      ? [line('Freight and other', row.otherApOutstanding, 0,
        'billed by a supplier but never designed into the quote, so there is no total to compare against')]
      : []),
  ];

  /**
   * Fixed widths on the money columns, and a label column that takes the rest.
   *
   * Three right-aligned figures with no width of their own collapse into each
   * other the moment the panel is narrow — "$15,850$143$15,707" is one cell's
   * worth of space holding three numbers. Fixing the money columns and letting
   * only the label flex keeps the gutters whatever the panel is given.
   */
  const COL = 132;

  const cell = (value, color, weight) => (
    <td style={{
      textAlign: 'right',
      padding: '4px 0 4px 16px',
      color,
      fontVariantNumeric: 'tabular-nums',
      fontWeight: weight || 400,
      whiteSpace: 'nowrap',
    }}
    >
      {money(value)}
    </td>
  );

  const Side = ({ title, lines, total, settledTotal, outstandingTotal, tone, footer }) => (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: INK, marginBottom: 6 }}>{title}</div>
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <colgroup>
          <col />
          <col style={{ width: COL }} />
          <col style={{ width: COL }} />
          <col style={{ width: COL }} />
        </colgroup>
        <thead>
          <tr style={{ color: FAINT }}>
            <th style={{ textAlign: 'left', fontWeight: 400, paddingBottom: 4 }}>Designed</th>
            <th style={{ textAlign: 'right', fontWeight: 400, paddingBottom: 4, paddingLeft: 16 }}>Total</th>
            <th style={{ textAlign: 'right', fontWeight: 400, paddingBottom: 4, paddingLeft: 16 }}>
              {tone === 'owed' ? 'Paid' : 'Received'}
            </th>
            <th style={{ textAlign: 'right', fontWeight: 400, paddingBottom: 4, paddingLeft: 16 }}>O/S</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr
              key={l.key}
              style={{ borderTop: '1px solid #F2F4F7', opacity: l.muted ? 0.6 : 1 }}
            >
              <td style={{ padding: '4px 8px 4px 0', color: INK }}>
                {l.label}
                {l.note && (
                  <div style={{ color: FAINT, fontSize: 10, lineHeight: 1.5 }}>{l.note}</div>
                )}
              </td>
              {cell(l.designed, TOTAL_INK)}
              {cell(l.settled, RECEIVED)}
              {cell(l.outstanding, tone === 'owed' ? OWED : INK, 500)}
            </tr>
          ))}
          <tr style={{ borderTop: '1px solid #E4E9F0', fontWeight: 600 }}>
            <td style={{ padding: '5px 8px 0 0', color: INK }}>Total</td>
            {cell(total, TOTAL_INK, 600)}
            {cell(settledTotal, RECEIVED, 600)}
            {cell(outstandingTotal, tone === 'owed' ? OWED : INK, 600)}
          </tr>
        </tbody>
      </table>
      {footer && (
        <div style={{ fontSize: 10, color: FAINT, marginTop: 6, lineHeight: 1.6 }}>{footer}</div>
      )}
    </div>
  );


  /*
   * Stacked, not side by side.
   *
   * Two halves sharing a row each got half the width, and the half that was
   * already further right lost its gutters first — the payable figures ran
   * together into one unreadable string. Full width apiece costs a little
   * scrolling and buys three columns that always line up, which is the whole
   * point of putting designed next to actual.
   */
  return (
    <div style={{ padding: '12px 16px 16px', background: '#FBFCFD' }}>
      <Row gutter={[0, 20]}>
        <Col xs={24}>
          <Side
            title="Receivable"
            lines={arLines}
            total={row.arTotal}
            settledTotal={row.arReceived}
            outstandingTotal={row.arOutstanding}
            footer={row.rebateDiscountShortfall > 0 && (
              <>
                The sale deducted {money(row.rebateArTotal + row.rebateDiscountShortfall)} of
                certificates from the customer and {money(row.rebateArTotal)} was claimed back
                — {money(row.rebateDiscountShortfall)} of the discount given was never
                recovered. It is a margin loss, not a receivable, so it is not counted above.
              </>
            )}
          />
        </Col>
        <Col xs={24}>
          <Side
            title="Payable"
            lines={apLines}
            total={row.apTotal}
            settledTotal={row.apTotal - row.apOutstanding}
            outstandingTotal={row.apOutstanding}
            tone="owed"
            footer={(
              <>
                {money(row.apOnBills)} of what is outstanding sits on supplier bills
                already raised; {money(row.apNotYetBilled)} is designed cost nothing has
                invoiced us for yet.
                {row.contingency > 0 && ` A further ${money(row.contingency)} of design contingency is excluded \u2014 it sits in the worst-case margin and not the best.`}
                {!row.hasDesign && ' This job has no valid quote, so there is no designed cost to compare against.'}
              </>
            )}
          />
        </Col>
      </Row>
    </div>
  );
}

function ProjectTable({ rows, narrow }) {
  const [openAr, setOpenAr] = useState(false);
  const [openAp, setOpenAp] = useState(false);

  const billedTitle = (billedInclGst, total) => {
    if (!(billedInclGst > 0)) return 'Nothing billed to us yet — the designed cost plus GST';
    if (billedInclGst >= total - 1) return 'Fully billed — what is still unpaid on those supplier bills';
    return `${money(billedInclGst)} billed so far, against ${money(total)} designed`;
  };

  const pair = (outKey, totKey, label, render) => ([
    { title: `${label} O/S`,
      dataIndex: outKey,
      key: outKey,
      align: 'right',
      width: 118,
      render: render || ((v) => <Money v={v} />) },
    { title: `${label} total`,
      dataIndex: totKey,
      key: totKey,
      align: 'right',
      width: 118,
      render: (v) => <Money v={v} tone="total" /> },
  ]);

  const columns = [
    { title: 'Project',
      dataIndex: 'projectId',
      key: 'projectId',
      fixed: narrow ? undefined : 'left',
      width: 84 },

    /**
     * Net first, because it is the answer and everything after it is working.
     * The two sides stay folded until asked for: a reader scanning for jobs
     * that are underwater needs one column, and a reader checking one of those
     * jobs needs all thirteen.
     */
    { title: 'Net O/S',
      dataIndex: 'netOutstanding',
      key: 'netOutstanding',
      align: 'right',
      width: 118,
      sorter: (a, b) => a.netOutstanding - b.netOutstanding,
      render: (v) => <Money v={v} bold tone={v < 0 ? 'owed' : 'outstanding'} /> },

    { title: groupTitle('AR', openAr, () => setOpenAr(!openAr)),
      children: [
        { title: 'AR O/S',
          dataIndex: 'arOutstanding',
          key: 'arOutstanding',
          align: 'right',
          width: 118,
          sorter: (a, b) => a.arOutstanding - b.arOutstanding,
          render: (v) => <Money v={v} bold /> },
        { title: 'AR total',
          dataIndex: 'arTotal',
          key: 'arTotal',
          align: 'right',
          width: 112,
          render: (v) => <Money v={v} tone="total" /> },
        ...(openAr ? [
          ...pair('customerArOutstanding', 'customerArTotal', 'Customer', (v, row) => (
            <AntTooltip title={row.customerArSource === 'xero'
              ? 'PPA \u2014 receipts read from Xero as the paid part of its invoices'
              : 'Marked collected in the CRM, by the customer or the finance provider'}
            >
              <Money v={v} />
            </AntTooltip>
          )),
          ...pair('rebateArOutstanding', 'rebateArTotal', 'Rebate'),
        ] : []),
      ] },

    { title: groupTitle('AP', openAp, () => setOpenAp(!openAp)),
      children: [
        { title: 'AP O/S',
          dataIndex: 'apOutstanding',
          key: 'apOutstanding',
          align: 'right',
          width: 118,
          sorter: (a, b) => a.apOutstanding - b.apOutstanding,
          render: (v) => <Money v={v} tone="owed" bold /> },
        { title: 'AP total',
          dataIndex: 'apTotal',
          key: 'apTotal',
          align: 'right',
          width: 112,
          render: (v) => <Money v={v} tone="total" /> },
        ...(openAp ? [
          ...pair('equipmentApOutstanding', 'equipmentApTotal', 'Equipment', (v, row) => (
            <AntTooltip title={billedTitle(row.equipmentApBilled, row.equipmentApTotal)}><Money v={v} /></AntTooltip>
          )),
          ...pair('installApOutstanding', 'installApTotal', 'Install', (v, row) => (
            <AntTooltip title={billedTitle(row.installApBilled, row.installApTotal)}><Money v={v} /></AntTooltip>
          )),
        ] : []),
      ] },

    { title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 170,
      ellipsis: true,
      render: (v) => <span style={{ color: INK }}>{v || '\u2014'}</span> },
    { title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 116,
      ellipsis: true,
      render: (v) => v || '\u2014' },
    { title: 'PV',
      dataIndex: 'pvSize',
      key: 'pvSize',
      align: 'right',
      width: 80,
      render: (v) => (v > 0 ? `${v} kW` : '\u2014') },
    { title: 'Battery',
      dataIndex: 'batterySize',
      key: 'batterySize',
      align: 'right',
      width: 92,
      render: (v) => (v > 0 ? `${v} kWh` : '\u2014') },
    { title: 'Closing',
      dataIndex: 'closingDate',
      key: 'closingDate',
      width: 102,
      sorter: (a, b) => new Date(a.closingDate) - new Date(b.closingDate),
      render: (v) => (v ? dayjs(v).format('D MMM YY') : '\u2014') },
    { title: 'Days',
      dataIndex: 'waitingDays',
      key: 'waitingDays',
      align: 'right',
      width: 70,
      // Longest wait first: the top of this table should be the jobs that have
      // been sitting the longest, not whichever one happens to sort first.
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.waitingDays - b.waitingDays,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span> },

    /**
     * How much of the wait the customer asked for.
     *
     * The days column counts every day since closing, which on a job the
     * customer paused is not our delay. These three say so: when they stopped
     * it, when it restarted, and how long that was - with an open hold left
     * without a resume date rather than being given today's, since no restart
     * has happened.
     */
    { title: 'Hold from',
      dataIndex: 'holdFrom',
      key: 'holdFrom',
      width: 104,
      render: (v, row) => (v
        ? (
          <AntTooltip title={row.holdSuspect || undefined}>
            <span style={{ color: row.holdSuspect ? OWED : INK }}>
              {dayjs(v).format('D MMM YY')}
            </span>
          </AntTooltip>
        )
        : <span style={{ color: FAINT }}>&mdash;</span>) },
    { title: 'Resumed',
      dataIndex: 'holdTo',
      key: 'holdTo',
      width: 104,
      render: (v, row) => {
        if (v) return dayjs(v).format('D MMM YY');
        if (row.holdOpen) return <Tag color="orange" style={{ marginInlineEnd: 0 }}>on hold</Tag>;
        return <span style={{ color: FAINT }}>&mdash;</span>;
      } },
    { title: 'Hold days',
      dataIndex: 'holdDays',
      key: 'holdDays',
      align: 'right',
      width: 92,
      sorter: (a, b) => (a.holdDays || 0) - (b.holdDays || 0),
      render: (v) => (v == null
        ? <span style={{ color: FAINT }}>&mdash;</span>
        : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>) },
  ];

  const extraCols = (openAr ? 4 : 0) + (openAp ? 4 : 0);

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey="projectId"
      size="small"
      scroll={{ x: 1520 + (extraCols * 118) }}
      expandable={{ expandedRowRender: (row) => <Breakdown row={row} /> }}
      pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (n) => `${n} projects` }}
    />
  );
}

/* -------------------------------------------------------------- the block */

function WaitingBlock({ data }) {
  const [measure, setMeasure] = useState('netOutstanding');
  const [filter, setFilter] = useState({ cls: null, bucket: null });
  const narrow = useNarrow();

  const classes = useMemo(() => data.classes || [], [data.classes]);
  const allProjects = useMemo(() => data.projects || [], [data.projects]);
  const buckets = useMemo(() => data.buckets || [], [data.buckets]);
  const r = data.reconciliation || {};

  /** The rows the whole page is reading, once the filter has had its say. */
  const rows = useMemo(() => allProjects.filter(
    (p) => (filter.cls == null || p.paymentClass === filter.cls)
      && (filter.bucket == null || p.bucket === filter.bucket),
  ), [allProjects, filter]);

  const totals = useMemo(() => {
    const out = { total: totalise(rows) };
    for (const c of classes) out[c] = totalise(rows.filter((p) => p.paymentClass === c));
    return out;
  }, [rows, classes]);
  const t = totals.total;

  /**
   * The chart always draws the whole population, never the filtered rows.
   *
   * It is the control, not the output. A chart that redrew itself to show only
   * the bar just clicked would leave nothing to click next and no way to see
   * where the selection sits among the rest.
   */
  const chartData = useMemo(() => buckets.map((b) => {
    const row = { label: b.label, bucket: b.bucket, count: b.total.count };
    for (const c of classes) row[c] = b[c][measure];
    return row;
  }), [buckets, classes, measure]);

  const pickBucket = useCallback((bucket) => {
    setFilter((f) => ({ ...f, bucket: f.bucket === bucket ? null : bucket }));
  }, []);
  const pickClass = useCallback((cls) => {
    setFilter((f) => ({ ...f, cls: (cls == null || f.cls === cls) ? null : cls }));
  }, []);

  const active = filter.cls != null || filter.bucket != null;
  const bucketLabel = filter.bucket == null ? null
    : (buckets.find((b) => b.bucket === filter.bucket)?.label || null);

  return (
    <>
      {active && (
        <Space size={6} wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: NEUTRAL }}>Showing</span>
          {filter.cls && (
            <Tag closable onClose={() => pickClass(filter.cls)} color="blue" style={{ marginInlineEnd: 0 }}>
              {filter.cls}
            </Tag>
          )}
          {bucketLabel && (
            <Tag closable onClose={() => pickBucket(filter.bucket)} color="blue" style={{ marginInlineEnd: 0 }}>
              waiting {bucketLabel}
            </Tag>
          )}
          <span style={{ fontSize: 12, color: FAINT }}>
            {rows.length} of {allProjects.length} projects
          </span>
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setFilter({ cls: null, bucket: null })}
            style={{ fontSize: 11, color: NEUTRAL }}
          >
            clear
          </Button>
        </Space>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
            <Statistic title="Closed, no welcome pack" value={t.count} valueStyle={{ color: INK }} />
            <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
              as at {dayjs().subtract(1, 'day').format('D MMM YYYY')}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title="Outstanding AR"
              value={t.arOutstanding}
              formatter={(v) => money(v)}
              valueStyle={{ color: INK }}
            />
            <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
              of {money(t.arTotal)} · {money(t.arReceived)} already in
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title="Outstanding AP"
              value={t.apOutstanding}
              formatter={(v) => money(v)}
              valueStyle={{ color: OWED }}
            />
            <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
              of {money(t.apTotal)} · {money(t.apNotYetBilled)} not yet invoiced to us
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
            <Statistic
              title="Net Outstanding"
              value={t.netOutstanding}
              formatter={(v) => money(v)}
              valueStyle={{ color: t.netOutstanding < 0 ? OWED : INK, fontWeight: 600 }}
            />
            <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>AR less AP, both outstanding</div>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '12px 16px' }}
        title={<span style={{ fontSize: 13 }}>Waiting since closing</span>}
        extra={(
          <Segmented
            size="small"
            value={measure}
            onChange={setMeasure}
            options={[
              { label: 'Net', value: 'netOutstanding' },
              { label: 'AR', value: 'arOutstanding' },
              { label: 'AP', value: 'apOutstanding' },
              { label: 'Projects', value: 'count' },
            ]}
          />
        )}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
            onClick={(e) => {
              const b = e?.activePayload?.[0]?.payload?.bucket;
              if (b != null) pickBucket(b);
            }}
          >
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: NEUTRAL }}
              interval={narrow ? 2 : 0}
              angle={-40}
              textAnchor="end"
              height={56}
            />
            <YAxis
              tick={{ fontSize: 11, fill: NEUTRAL }}
              tickFormatter={measure === 'count' ? (v) => v : moneyShort}
              width={56}
            />
            <Tooltip
              cursor={{ fill: 'rgba(47,127,168,0.06)' }}
              content={({ active: on, payload, label }) => {
                if (!on || !payload?.length) return null;
                const row = payload[0].payload;
                return (
                  <div style={{
                    background: '#fff', border: '1px solid #E4E9F0', borderRadius: 6,
                    padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                  >
                    <div style={{ fontWeight: 600, color: INK, marginBottom: 6 }}>Waiting {label}</div>
                    {payload.map((p) => (
                      <div key={p.dataKey} style={{ display: 'flex', gap: 16, justifyContent: 'space-between' }}>
                        <span style={{ color: p.color }}>{p.dataKey}</span>
                        <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>
                          {measure === 'count' ? p.value : money(p.value)}
                        </span>
                      </div>
                    ))}
                    <div style={{ color: FAINT, marginTop: 6 }}>
                      {row.count} project{row.count === 1 ? '' : 's'} · click to filter
                    </div>
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {classes.map((c) => (
              <Bar key={c} dataKey={c} stackId="a" fill={CLASS_COLOR[c]} cursor="pointer">
                {chartData.map((d) => (
                  <Cell
                    key={d.bucket}
                    fill={CLASS_COLOR[c]}
                    // Unselected bars dim rather than vanish, so a selection is
                    // still read against the shape it was taken from.
                    fillOpacity={filter.bucket == null || filter.bucket === d.bucket ? 1 : 0.22}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 11, color: FAINT, marginTop: 6 }}>
          Fortnightly buckets from the closing date, over the last {r.waitingMonths} months.
          Click a bar to filter the page; click it again to clear.
        </div>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: 0 }}
        title={<span style={{ fontSize: 13 }}>Where the money sits</span>}
        extra={(
          <span style={{ fontSize: 11, color: FAINT }}>
            click a row to filter · click AR or AP to expand
          </span>
        )}
      >
        <SummaryTable
          classes={classes}
          totals={totals}
          filter={filter}
          onPickClass={pickClass}
          narrow={narrow}
        />
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: 0 }}
        title={<span style={{ fontSize: 13 }}>Project by project</span>}
      >
        <ProjectTable rows={rows} narrow={narrow} />
      </Card>

      {r.costSource === 'unavailable' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Payable is understated"
          description="The quote database that holds each job's designed cost did not answer, so the payable here is only the supplier bills already raised. The designed cost of everything not yet invoiced to us — normally the larger half — is missing, and the net position is correspondingly too high."
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How the two ledgers were reconciled"
        description={(
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>
              <b>Totals</b> on both sides come from the designed breakdown — the same
              quote figures the Snapshot tab shows in its designed column. Everything
              is stated inclusive of GST, because this is a question about cash and GST
              is part of the cash. The decomposition was checked rather than assumed:
              GST is charged on the whole system and the rebate is then deducted from
              the inclusive price, so customer AR is <i>GST × 11 − rebate</i>, which
              matches the CRM&rsquo;s own final price at 1.0000 on the tenth, fiftieth
              and ninetieth percentile of 234 jobs. Certificates are grossed up too —
              selling them to a trading agent is a taxable supply, confirmed on 1,437
              of 1,441 clearing rows — while state rebates are not a sale and stay at
              face value.
            </div>
            <div style={{ marginTop: 6 }}>
              <b>Customer AR</b> is marked off in the CRM for upfront and BNPL: the
              customer in one case, the finance provider in the other, both recorded as
              a collection date against the stage they settled. A PPA is not staged at
              all — nobody is buying anything — so its receipts come from Xero instead,
              as the paid portion of its own invoices. Certificate invoices raised to
              the trading agent carry the same project number as the customer&rsquo;s
              own, so they are told apart by the counterparty and kept out of this
              column — otherwise a PPA would count certificate money here and again
              under rebate.
            </div>
            <div style={{ marginTop: 6 }}>
              <b>Rebate AR</b> is grossed for GST only on a PPA, which sells its
              certificates commercially. A residential upfront or financed sale does
              not, and the figure the CRM holds is already the cash — on one checked
              job the CRM has 91 × $39.25 = $3,571.75 of solar certificates and
              Xero&rsquo;s invoice to the agent is $3,571.75 to the cent. State grants
              are never grossed on any method, since a grant is not a sale. The CRM
              battery figure is net of the agent&rsquo;s fee, a flat dollar per
              certificate, which is the right basis here: that dollar is never ours,
              and the fee arrives as its own supplier bill where it is already counted.
              Receipts come from the CRM, the only record carrying a collection date
              per scheme;
              {' '}{money(t.rebateArEstimated)} of what is outstanding is still an
              estimate, since the claim cannot be lodged until the system is installed
              and none of these are.
            </div>
            <div style={{ marginTop: 6 }}>
              <b>AP</b> is decided one cost item at a time, because a job can have its
              hardware billed and its installation not. For equipment and installation
              separately, the Xero ledger is asked whether anything has been posted to
              that category for that job. Where something has, what is still unpaid
              comes from the supplier invoices, split across the lines that make them
              up; those carry the project number in the invoice <i>number</i> rather
              than the reference, which reads &ldquo;SI-00016179 Rhoda Pooley
              126709&rdquo;. Where nothing has, nothing is billed and the whole designed
              amount plus GST is still to come.
            </div>
            <div style={{ marginTop: 6, color: NEUTRAL }}>
              Design contingency is carried but not counted ({money(t.contingency)} here):
              it sits in the worst-case margin and not the best, which is to say nobody
              has agreed to spend it. Invoices scanned from {r.xeroScannedFrom}
              {' '}({(r.xeroScanned?.ACCREC || 0).toLocaleString()} customer,
              {' '}{(r.xeroScanned?.ACCPAY || 0).toLocaleString()} supplier outstanding).
              {r.unmappedPaymentMethods?.length > 0 && ` Unmapped payment methods: ${r.unmappedPaymentMethods.join(', ')}.`}
            </div>
          </div>
        )}
      />
    </>
  );
}

/* ------------------------------------------------------------------ page */

export default function CurrentPipeline() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get('/api/retail-finance/pipeline', { params: { block: 'waiting' }, timeout: 180000 })
      .then((res) => { if (live) { setData(res.data?.data?.waiting || null); setError(null); } })
      .catch((e) => { if (live) setError(e.response?.data?.message || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  if (error) {
    return <Alert type="error" showIcon message="Could not load the pipeline" description={error} />;
  }

  return (
    <Spin spinning={loading} tip="Reconciling the CRM against the invoice book…">
      <div style={{ minHeight: 200 }}>
        {data && <WaitingBlock data={data} />}
      </div>
    </Spin>
  );
}
